import classnames from 'classnames'
import { FormikProvider, useFormik } from 'formik'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import allowedUserRoles from '../../../state/calculations/permissions/allowedUserRoles'
import useCreateUser from '../../../state/mutations/useCreateUser'
import { useSession } from '../../../state/session'
import useRoutes from '../../../state/useRoutes'
import setFormErrors from '../../../utilities/setFormErrors'
import useDialog from '../../../utilities/useDialog'
import { CheckboxFormik } from '../../basics/Checkbox/Checkbox'
import FormButtons from '../../basics/FormButtons/FormButtons'
import MessageBox from '../../basics/MessageBox/MessageBox'
import { SelectFormik } from '../../basics/Select/Select'
import { TextInputFormik } from '../../basics/TextInput/TextInput'

import styles from './CreateUserForm.module.scss'
import { OnboardingDialog } from './OnboardingDialog'

/**********************************************************************
 * CreateUserForm
 **********************************************************************/
interface CreateUserFormProps {
  onClose: () => void
}

export default React.memo(function CreateUserForm({ onClose }: CreateUserFormProps) {
  const { t } = useTranslation()
  const routes = useRoutes()
  const showDialog = useDialog()
  const { user: currentUser } = useSession()

  const userRoleOptions = useMemo(
    () =>
      Object.entries(allowedUserRoles(currentUser))
        .filter(([_, allowed]) => allowed)
        .map(([value]) => ({
          value,
          label: t(`user.role.${value}`),
        })),
    [t, currentUser?.permissions]
  )

  const usernameSuffix = currentUser?.client.code

  const createUser = useCreateUser()
  const formik = useFormik({
    initialValues: { firstname: '', lastname: '', position: '', username: '', email: '', role: 'user', isActive: true },
    validate: (values) => {
      const errors: any = {}
      if (!values.email && !values.username) {
        errors.email = t('errors.username or email required')
        errors.username = t('errors.username or email required')
      }
      return errors
    },
    onSubmit: async (values, { setStatus, setErrors }) => {
      try {
        const username = values.username ? `${values.username}#${usernameSuffix}` : undefined
        const { uuid, url, initialPassword } = await createUser.mutateAsync({
          ...values,
          username,
        })

        if (url) {
          await showDialog((close) => (
            <MessageBox close={close}>
              <OnboardingDialog
                username={username!}
                password={initialPassword!}
                link={url}
                name={`${values.firstname} ${values.lastname}`}
              />
            </MessageBox>
          ))
        }

        routes.manageUser.goto({ uuid }, { replace: true })
      } catch (err) {
        setFormErrors({ setStatus, setErrors, t }, err)
      }
    },
  })

  return (
    <FormikProvider value={formik}>
      <form noValidate onSubmit={formik.handleSubmit} className={classnames(styles.CreateUserForm)}>
        <header>
          <hgroup>
            <h1>{t('manage user.create user')}</h1>
          </hgroup>
        </header>

        <section>
          <TextInputFormik
            autoComplete="off"
            name="firstname"
            label={t('user.firstname')}
            required
            disabled={formik.isSubmitting}
          />
          <TextInputFormik
            autoComplete="off"
            name="lastname"
            label={t('user.lastname')}
            required
            disabled={formik.isSubmitting}
          />
          <TextInputFormik
            autoComplete="off"
            name="position"
            label={t('user.field position')}
            disabled={formik.isSubmitting}
          />
          <TextInputFormik
            autoComplete="off"
            type="email"
            name="email"
            label={t('user.emailAddress')}
            required={false}
            disabled={formik.isSubmitting}
          />
          <TextInputFormik
            autoComplete="off"
            name="username"
            label={t('user.field username')}
            required={false}
            disabled={formik.isSubmitting}
            after={<span>#{usernameSuffix}</span>}
          />
          <SelectFormik
            name="role"
            label={t('user.field role')}
            options={userRoleOptions}
            required
            disabled={formik.isSubmitting}
          />
          <CheckboxFormik
            omitErrorDisplay
            name="isActive"
            label={t('user.user_status.active')}
            disabled={formik.isSubmitting}
          />
        </section>

        <FormButtons submitLabel={t('Create')} onClose={onClose} {...formik} />
      </form>
    </FormikProvider>
  )
})
